/**
 * i18n Configuration
 * Internationalization setup using svelte-i18n
 */

import { derived, writable } from 'svelte/store';
import {
  init,
  register,
  addMessages,
  locale,
  waitLocale,
  t,
  _,
  locales as availableLocales,
  isLoading,
} from 'svelte-i18n';

import en from '../../locales/en.json';

addMessages('en', en);

// Supported languages (EU + CJK + PT-BR + Hindi)
export const SUPPORTED_LANGUAGES = [
  'en',
  'es',
  'fr',
  'de',
  'it',
  'zh-CN',
  'zh-TW',
  'ja',
  'ko',
  'pt-BR',
  'hi',
] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

// Language display names (native)
export const LANGUAGE_NAMES: Record<SupportedLanguage, string> = {
  en: 'English',
  es: 'Español',
  fr: 'Français',
  de: 'Deutsch',
  it: 'Italiano',
  'zh-CN': '简体中文',
  'zh-TW': '繁體中文',
  ja: '日本語',
  ko: '한국어',
  'pt-BR': 'Português (BR)',
  hi: 'हिन्दी',
};

// Preload English synchronously so SSR has translations available

// Register additional locale files for lazy loading on the client
register('es', () => import('../../locales/es.json'));
register('fr', () => import('../../locales/fr.json'));
register('de', () => import('../../locales/de.json'));
register('it', () => import('../../locales/it.json'));
register('zh-CN', () => import('../../locales/zh-CN.json'));
register('zh-TW', () => import('../../locales/zh-TW.json'));
register('ja', () => import('../../locales/ja.json'));
register('ko', () => import('../../locales/ko.json'));
register('pt-BR', () => import('../../locales/pt-BR.json'));
register('hi', () => import('../../locales/hi.json'));

function normalizeBrowserLanguage(raw: string): SupportedLanguage | null {
  const lower = raw.toLowerCase();
  if (lower.startsWith('zh-tw') || lower.startsWith('zh-hant') || lower === 'zh-hk' || lower === 'zh-mo') {
    return 'zh-TW';
  }
  if (lower === 'zh' || lower.startsWith('zh-cn') || lower.startsWith('zh-hans')) {
    return 'zh-CN';
  }
  if (lower.startsWith('pt')) {
    return 'pt-BR';
  }
  // Exact BCP-47 match first (e.g. zh-CN)
  if (isSupportedLanguage(raw)) return raw;
  const primary = raw.split('-')[0];
  if (primary === 'zh') return 'zh-CN';
  if (primary === 'pt') return 'pt-BR';
  return isSupportedLanguage(primary) ? (primary as SupportedLanguage) : null;
}

// Helper for client-side preferred locale detection
function getPreferredClientLocale(): SupportedLanguage {
  if (typeof window === 'undefined') return 'en';

  const stored = localStorage.getItem('quilty.lang');
  if (stored && isSupportedLanguage(stored)) return stored;

  const fromNavigator = normalizeBrowserLanguage(navigator.language);
  return fromNavigator ?? 'en';
}

// Initialize i18n with English to guarantee a locale for SSR
init({
  fallbackLocale: 'en',
  initialLocale: 'en',
});

// Track if i18n is ready
// Use a combination of isLoading and a manual flag to ensure locale is actually loaded
const manualReady = writable(false);
export const i18nReady = derived(
  [isLoading, manualReady],
  ([$isLoading, $manualReady]) => !$isLoading && $manualReady
);

// Ensure locale is ready before rendering
if (typeof window !== 'undefined') {
  const preferredLocale = getPreferredClientLocale();

  (async () => {
    try {
      await waitLocale('en');
      if (preferredLocale !== 'en') {
        await setLocale(preferredLocale);
      }
    } finally {
      manualReady.set(true);
    }
  })();
} else {
  manualReady.set(true);
}

/**
 * Check if a language code is supported
 */
export function isSupportedLanguage(lang: string): lang is SupportedLanguage {
  return SUPPORTED_LANGUAGES.includes(lang as SupportedLanguage);
}

/**
 * Get the current locale
 */
export const currentLocale = derived(locale, $locale => $locale || 'en');

/**
 * Set the current locale
 */
export async function setLocale(lang: SupportedLanguage): Promise<void> {
  if (!isSupportedLanguage(lang)) {
    console.warn(`Unsupported language: ${lang}, falling back to 'en'`);
    lang = 'en';
  }
  locale.set(lang);
  await waitLocale(lang);
}

/**
 * Detect language from browser
 */
export function detectBrowserLanguage(): SupportedLanguage {
  if (typeof window === 'undefined') return 'en';

  return normalizeBrowserLanguage(navigator.language) ?? 'en';
}

/**
 * Get language from localStorage
 */
export function getStoredLanguage(): SupportedLanguage | null {
  if (typeof window === 'undefined') return null;

  const stored = localStorage.getItem('quilty.lang');
  return stored && isSupportedLanguage(stored) ? stored : null;
}

/**
 * Store language preference in localStorage
 */
export function storeLanguage(lang: SupportedLanguage): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem('quilty.lang', lang);
}

// Re-export translation functions
export { t, _, waitLocale, availableLocales };
